import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import { Download, Eye, FileText, MoreHorizontal } from 'lucide-react';
import '../risk-plus/report.css';
import './impact-report.css';
import { DashboardStore } from '../../stores/DashboardStore';
import UseCaseChatStore from '../../stores/UseCaseChatStore';
import { ImpactPlusRegisterHeaderStore } from '../../stores/ImpactPlusRegisterHeaderStore';
import RiskPlusReportStore from '../../stores/RiskPlusReportStore';
import { ImpactPlusReportStore } from '../../stores/ImpactPlusReportStore';
import { ImpactReportDetailModal } from '../ImpactPlusReport/ImpactReportModal';
import { RenderImpactPlusReport } from '../ImpactPlusReport/ImpactPlusReport.renderer';
import { downloadImpactReportPdfFromDom } from './impactReportPdfFromDom';
import { ReportDetailModal as RiskReportDetailModal } from '../RiskPlusReport/RiskReportModal';
import { useReportModalParam } from '../../hooks/useReportModalParam';

const STATUS_BADGE_CLASS =
  'inline-flex max-w-full items-center rounded-full bg-[#8B1515] px-2.5 py-1 text-xxxs font-semibold leading-tight text-white sm:text-xxs';
const NEUTRAL_BADGE_CLASS =
  'inline-flex items-center rounded-md bg-[#ECEBE8] px-2.5 py-1 text-xxxs font-medium text-gray-800 sm:text-xxs';

type AssessmentType = 'impact_plus' | 'risk_plus';

type ReportListItem = {
  id: string;
  useCaseChatId: string;
  reportId: string;
  title: string;
  impactRef: string;
  generatedLabel: string;
  version: string;
  statusLabel: string;
  entityLabel: string;
  groupsLabel: string;
  harmsLabel: string;
};

type UnifiedReportItem = ReportListItem & {
  assessmentType: AssessmentType;
};

const ReportsEmptyState = ({
  title,
  description,
  ctaLabel,
  onCtaClick,
  ctaDisabled = false,
  hint,
}: {
  title: string;
  description: string;
  ctaLabel: string;
  onCtaClick: () => void;
  ctaDisabled?: boolean;
  hint?: string;
}) => (
  <div className="empty-state flex flex-col items-center justify-center px-6 py-16 text-center">
    <div
      className="empty-icon mb-4 grid h-[52px] w-[52px] place-items-center rounded-[14px] border"
      style={{ background: '#E6F1FB', borderColor: '#B5D4F4' }}
      aria-hidden
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 22 22"
        fill="none"
        stroke="#185FA5"
        strokeWidth="1.4"
      >
        <rect x="2" y="2" width="8" height="8" rx="1.5" />
        <rect x="12" y="2" width="8" height="8" rx="1.5" />
        <rect x="2" y="12" width="8" height="8" rx="1.5" />
        <rect x="12" y="12" width="8" height="8" rx="1.5" opacity=".35" />
      </svg>
    </div>
    <div
      className="empty-title mb-1.5 text-sm font-semibold tracking-[-0.2px]"
      style={{ color: '#1A1A18' }}
    >
      {title}
    </div>
    <div
      className="empty-desc mb-5 max-w-[320px] text-xs leading-[1.65]"
      style={{ color: '#999893' }}
    >
      {description}
    </div>
    <button
      type="button"
      onClick={onCtaClick}
      disabled={ctaDisabled}
      className="empty-cta inline-flex items-center gap-1.5 rounded-[8px] px-5 py-[9px] text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
      style={{ background: '#0D3D6B' }}
      onMouseEnter={(e) => {
        if (ctaDisabled) return;
        (e.currentTarget as HTMLButtonElement).style.background = '#072040';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = '#0D3D6B';
      }}
    >
      {ctaLabel}
    </button>
    {hint ? (
      <div className="empty-hint mt-2.5 text-xxs" style={{ color: '#C8C6C0' }}>
        {hint}
      </div>
    ) : null}
  </div>
);

function normalizeAssessmentType(typeOrTitle: string | null | undefined): AssessmentType {
  const t = String(typeOrTitle ?? '').toLowerCase();
  if (t.includes('risk')) return 'risk_plus';
  return 'impact_plus';
}

function ReportCard({
  item,
  actions,
  onTitleClick,
  assessmentTags,
}: {
  item: UnifiedReportItem;
  actions: React.ReactNode;
  onTitleClick: () => void;
  assessmentTags: AssessmentType[];
}) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-gray-200/90 bg-white p-4 shadow-sm sm:flex-row sm:items-start sm:gap-5 sm:p-5">
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#FCE4E4]"
        aria-hidden
      >
        <FileText className="h-5 w-5 text-[#8B1515]" strokeWidth={2} />
      </div>

      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onTitleClick}
          className="block w-full text-left text-sm font-bold leading-snug text-gray-900 hover:underline"
        >
          {item.title}
        </button>
        <p className="mt-1.5 text-xxs text-gray-500 sm:text-xxs">
          {item.impactRef || item.useCaseChatId}
          {item.generatedLabel ? (
            <>
              <span className="mx-1.5 text-gray-500">·</span>
              {item.generatedLabel}
            </>
          ) : null}
          {item.version ? (
            <>
              <span className="mx-1.5 text-gray-500">·</span>
              {item.version}
            </>
          ) : null}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {item.statusLabel ? <span className={STATUS_BADGE_CLASS}>{item.statusLabel}</span> : null}
          {assessmentTags.map((t) => (
            <span key={t} className={NEUTRAL_BADGE_CLASS}>
              {t === 'impact_plus' ? 'Impact Plus' : 'Risk Plus'}
            </span>
          ))}
          {item.entityLabel ? <span className={NEUTRAL_BADGE_CLASS}>{item.entityLabel}</span> : null}
          {item.groupsLabel ? <span className={NEUTRAL_BADGE_CLASS}>{item.groupsLabel}</span> : null}
          {item.harmsLabel ? <span className={NEUTRAL_BADGE_CLASS}>{item.harmsLabel}</span> : null}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 sm:pl-2">{actions}</div>
    </div>
  );
}

const ImpactPlusReportsList = () => {
  const navigate = useNavigate();
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const impactReportModal = useReportModalParam('impactPlusReportModal');
  const riskReportModal = useReportModalParam('riskPlusReportModal');
  const [isDownloading, setIsDownloading] = useState(false);
  const [pendingDownloadReportId, setPendingDownloadReportId] = useState<string | null>(null);
  const [pendingDownloadTitle, setPendingDownloadTitle] = useState<string | null>(null);
  const [pendingDownloadType, setPendingDownloadType] = useState<AssessmentType | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const registerSearchQuery = ImpactPlusRegisterHeaderStore((s) => s.registerSearchQuery);
  const setCreateNewHandler = ImpactPlusRegisterHeaderStore((s) => s.setCreateNewHandler);

  const loadUseCaseBriefs = DashboardStore.useDashboardStore((state) => state.loadUseCaseBriefs);
  const useCaseBriefs = DashboardStore.useDashboardStore((state) => state.useCaseBriefs);
  const createUseCaseChat = UseCaseChatStore.useUseCaseChatStore((state) => state.createUseCaseChat);

  const { loadReportData, setRiskPlusReport } = RiskPlusReportStore.useRiskPlusReportStore(
    useShallow((s) => ({
      loadReportData: s.loadReportData,
      setRiskPlusReport: s.setRiskPlusReport,
    })),
  );
  const reportLoading = RiskPlusReportStore.useRiskPlusReportStore((s) => s.isLoading);

  const loadImpactPlusReport = ImpactPlusReportStore.useImpactPlusReportStore((s) => s.loadImpactPlusReport);
  const setImpactPlusReport = ImpactPlusReportStore.useImpactPlusReportStore((s) => s.setImpactPlusReport);
  const impactPlusLoading = ImpactPlusReportStore.useImpactPlusReportStore((s) => s.isLoading);
  const impactPlusReportData = ImpactPlusReportStore.useImpactPlusReportStore((s) => s.impactPlusReport);

  const downloadPdfFromElement = useCallback(async (el: HTMLElement, filename: string) => {
    setIsDownloading(true);
    try {
      await downloadImpactReportPdfFromDom(el, filename);
    } finally {
      setIsDownloading(false);
    }
  }, []);

  const riskRows = useMemo<UnifiedReportItem[]>(() => {
    const out: UnifiedReportItem[] = [];
    for (const brief of useCaseBriefs) {
      for (const report of brief.reports ?? []) {
        const assessmentType = normalizeAssessmentType(report.reportType || report.reportTitle);
        if (assessmentType !== 'risk_plus') continue;
        out.push({
          id: `risk-${report.reportId}`,
          useCaseChatId: brief.useCaseChatId,
          reportId: report.reportId,
          title: report.reportTitle || 'Risk Plus Report',
          impactRef: '',
          generatedLabel: '',
          version: '',
          statusLabel: '',
          entityLabel: '',
          groupsLabel: '',
          harmsLabel: '',
          assessmentType,
        });
      }
    }
    return out;
  }, [useCaseBriefs]);

  const impactRows = useMemo<UnifiedReportItem[]>(() => {
    const out: UnifiedReportItem[] = [];
    for (const brief of useCaseBriefs) {
      for (const report of brief.reports ?? []) {
        const assessmentType = normalizeAssessmentType(report.reportType || report.reportTitle);
        if (assessmentType !== 'impact_plus') continue;
        out.push({
          id: `impact-${report.reportId}`,
          useCaseChatId: brief.useCaseChatId,
          reportId: report.reportId,
          title: report.reportTitle || 'Impact Plus Report',
          impactRef: '',
          generatedLabel: '',
          version: '',
          statusLabel: '',
          entityLabel: '',
          groupsLabel: '',
          harmsLabel: '',
          assessmentType,
        });
      }
    }
    return out;
  }, [useCaseBriefs]);

  const allRows = useMemo<UnifiedReportItem[]>(() => {
    return [...impactRows, ...riskRows];
  }, [impactRows, riskRows]);

  const assessmentTagsByUseCase = useMemo(() => {
    const map = new Map<string, Set<AssessmentType>>();
    for (const r of allRows) {
      if (!map.has(r.useCaseChatId)) map.set(r.useCaseChatId, new Set());
      map.get(r.useCaseChatId)!.add(r.assessmentType);
    }
    return map;
  }, [allRows]);

  useEffect(() => {
    if (!pendingDownloadReportId) return;
    const loading = pendingDownloadType === 'risk_plus' ? reportLoading : impactPlusLoading;
    const data = pendingDownloadType === 'risk_plus' ? null : impactPlusReportData;
    if (loading) return;
    if (!data) return;
    if (data.id !== pendingDownloadReportId) return;

    let cancelled = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 1600));
      if (cancelled) return;

      try {
        const el = printRef.current;
        if (!el) throw new Error('PDF renderer not mounted');
        const fallbackTitle = data?.titlePage?.title?.trim() || 'Impact Report';
        const raw = pendingDownloadTitle?.trim() || fallbackTitle;
        const safe = raw
          .replace(/[\\/:*?"<>|]+/g, '-')
          .replace(/\s+/g, ' ')
          .trim();
        const filename = `${safe}.pdf`;
        await downloadPdfFromElement(el, filename);
      } finally {
        if (!cancelled) setPendingDownloadReportId(null);
        if (!cancelled) setPendingDownloadTitle(null);
        if (!cancelled) setPendingDownloadType(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [downloadPdfFromElement, impactPlusReportData, pendingDownloadReportId, pendingDownloadTitle, pendingDownloadType, reportLoading, impactPlusLoading]);

  const filteredItems = useMemo(() => {
    const q = registerSearchQuery.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((row) => {
      const title = row.title.toLowerCase();
      const ref = row.impactRef.toLowerCase();
      const entity = row.entityLabel.toLowerCase();
      return title.includes(q) || ref.includes(q) || entity.includes(q);
    });
  }, [allRows, registerSearchQuery]);

  const handleCreateNewFromButton = useCallback(async () => {
    try {
      const id = await createUseCaseChat();
      const sourcePath = '/dashboard';
      const modeSuffix = 'new-impact';
      navigate(`/chat/${id}/${modeSuffix}`, { state: { from: sourcePath } });
      await loadUseCaseBriefs(true).catch(() => { });
    } catch (error) {
      console.error('Error initializing use case chat:', error);
      navigate('/chat/n');
    }
  }, [createUseCaseChat, navigate, loadUseCaseBriefs]);

  useEffect(() => {
    setCreateNewHandler(handleCreateNewFromButton);
    return () => {
      setCreateNewHandler(null);
      ImpactPlusRegisterHeaderStore.getState().setRegisterSearchQuery('');
    };
  }, [handleCreateNewFromButton, setCreateNewHandler]);

  useEffect(() => {
    void loadUseCaseBriefs(true).catch(() => { });
  }, [loadUseCaseBriefs]);

  useEffect(() => {
    if (!menuOpenId) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest(`[data-reports-menu-root="${menuOpenId}"]`)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [menuOpenId]);

  const handleViewUseCase = useCallback(
    (item: UnifiedReportItem) => {
      setMenuOpenId(null);
      navigate(
        `/use-case/${item.useCaseChatId}/stakeholder-register?impactPlusReportId=${encodeURIComponent(item.reportId)}`,
      );
    },
    [navigate],
  );

  const handleImpactReportModalClose = useCallback(() => {
    impactReportModal.close();
    setImpactPlusReport(null);
  }, [impactReportModal, setImpactPlusReport]);

  const handleRiskReportModalClose = useCallback(() => {
    riskReportModal.close();
    setRiskPlusReport(null);
  }, [riskReportModal, setRiskPlusReport]);

  const openReport = useCallback(
    (item: UnifiedReportItem) => {
      setMenuOpenId(null);
      if (item.assessmentType === 'risk_plus') {
        riskReportModal.open(item.reportId);
      } else {
        impactReportModal.open(item.reportId);
      }
    },
    [riskReportModal, impactReportModal],
  );

  useEffect(() => {
    if (!riskReportModal.reportId) return;
    void loadReportData(riskReportModal.reportId).catch((error) => {
      console.error('Error fetching report data:', error);
    });
  }, [riskReportModal.reportId, loadReportData]);

  useEffect(() => {
    if (!impactReportModal.reportId) return;
    void loadImpactPlusReport(impactReportModal.reportId).catch((error) => {
      console.error('Error fetching report data:', error);
    });
  }, [impactReportModal.reportId, loadImpactPlusReport]);

  const handleDownloadPdf = useCallback(
    async (item: UnifiedReportItem) => {
      setMenuOpenId(null);
      try {
        setPendingDownloadReportId(item.reportId);
        setPendingDownloadTitle(item.title);
        setPendingDownloadType(item.assessmentType);
        if (item.assessmentType === 'risk_plus') {
          await loadReportData(item.reportId);
        } else {
          await loadImpactPlusReport(item.reportId);
        }
      } catch (err) {
        console.error('PDF download failed:', err);
        setIsDownloading(false);
        setPendingDownloadReportId(null);
        setPendingDownloadTitle(null);
        setPendingDownloadType(null);
      }
    },
    [loadReportData, loadImpactPlusReport],
  );


  return (
    <div className="min-h-full bg-impact-plus-page p-4 pb-12 md:p-8 lg:py-10 lg:pt-6">
      <div className="mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl">Reports</h1>
          <p className="mt-1 text-sm text-gray-500 md:text-base">All Impact+ and Risk+ reports for your logged use cases are stored here</p>
        </header>

        <div className="flex flex-col gap-4">
          {filteredItems.length === 0 ? (
            <ReportsEmptyState
              title="No reports found"
              description={
                registerSearchQuery.trim().length > 0
                  ? 'No reports match your search.'
                  : 'Once you generate your first Impact+ or Risk+ report, it will appear here.'
              }
              ctaLabel="Back to dashboard"
              onCtaClick={() => navigate('/dashboard')}
            />
          ) : (
            filteredItems.map((item) => (
              <div key={item.id} className="relative" data-reports-menu-root={item.id}>
                <ReportCard
                  item={item}
                  onTitleClick={() => openReport(item)}
                  assessmentTags={[...(assessmentTagsByUseCase.get(item.useCaseChatId) ?? new Set())]}
                  actions={
                    <>
                      <button
                        type="button"
                        onClick={() => void handleDownloadPdf(item)}
                        disabled={isDownloading || reportLoading}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xxs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                      >
                        <Download className="h-4 w-4 text-gray-600" strokeWidth={2} />
                        {isDownloading ? 'Preparing…' : 'Download PDF'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setMenuOpenId((id) => (id === item.id ? null : item.id))}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50"
                        aria-label="More actions"
                        aria-expanded={menuOpenId === item.id}
                      >
                        <MoreHorizontal className="h-4 w-4" strokeWidth={2} />
                      </button>
                      {menuOpenId === item.id && (
                        <div className="absolute right-4 top-14 z-20 w-44 rounded-xl border border-gray-200 bg-white py-1 shadow-lg sm:right-5 sm:top-[4.1rem]">
                          <button
                            type="button"
                            onClick={() => handleViewUseCase(item)}
                            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xxs text-gray-700 hover:bg-gray-50"
                          >
                            <Eye size={16} className="text-gray-500" />
                            View use case
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDownloadPdf(item)}
                            disabled={isDownloading || reportLoading}
                            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xxs text-gray-700 hover:bg-gray-50"
                          >
                            <Download size={16} className="text-gray-500" />
                            {isDownloading ? 'Preparing…' : 'Download PDF'}
                          </button>
                        </div>
                      )}
                    </>
                  }
                />
              </div>
            ))
          )}
        </div>

        <ImpactReportDetailModal isOpen={impactReportModal.isOpen} onClose={handleImpactReportModalClose} />
        <RiskReportDetailModal isOpen={riskReportModal.isOpen} onClose={handleRiskReportModalClose} />

        <div
          className="fixed left-10000 top-0 overflow-visible bg-white pdf-capture-mode"
          style={{ width: 794 }}
          aria-hidden="true"
        >
          {impactPlusReportData && <RenderImpactPlusReport ref={printRef} reportData={impactPlusReportData} />}
        </div>
      </div>
    </div>
  );
};

export default ImpactPlusReportsList;
